import { FC, useCallback, useEffect, useState } from "react";
import useGetCollection from "../hooks/getCollection";
import Album from "../models/album";
import { InputAdornment, Skeleton, Stack, TextField, useTheme } from "@mui/material";
import ScreenBanner from "../components/screenBanner";
import latestAlbum from "../assets/latestAlbum.jpg";
import AlbumTile from "../components/albumTile";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ScreenBase from "./ScreenBase";

const AlbumScreen: FC = () => {
	const theme = useTheme();

	const { data: albumData, loading } = useGetCollection<Album[]>('/album/all');

	const [albums, setAlbums] = useState<Album[]>([]);
	const [filteredAlbums, setFilteredAlbums] = useState<Album[]>([]);
	const [filter, setFilter] = useState<string>('');

	const filterAlbums = useCallback((filter: string) => {
		if (albums && albums.length) {
			setFilteredAlbums(albums.filter((album) => album.name.toLowerCase().includes(filter.toLowerCase())));
		} else {
			setFilteredAlbums([]);
		}
	}, [albums]);

	useEffect(() => {
		setAlbums(albumData?.sort((a, b) => a.releaseDate && b.releaseDate && new Date(a.releaseDate).toISOString() < new Date(b.releaseDate).toISOString() ? 1 : -1) || []);
		setFilter('');
	}, [albumData]);

	useEffect(() => {
		filterAlbums(filter);
	}, [filter, filterAlbums]);
	
	return (
		<ScreenBase
			fixedChildren={(
				<Stack spacing={2}>
					<ScreenBanner
						title="Albums"
						bannerImage={latestAlbum}
						height={120}
						verticalAdjust="73%"
						brightnessAdjust={0.75}
					/>
					<Stack direction="row" spacing={2} alignItems="center">
						<TextField
							variant="outlined"
							size="small"
							sx={{ width: { xs: '100%', sm: '100%', md: '100%', lg: '80%', xl: 600 } }}
							placeholder="Find album by name or tag..."
							slotProps={{
								input: {
									startAdornment: (
										<InputAdornment position="start">
											<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} style={{ color: theme.palette.text.secondary }} />
										</InputAdornment>
									)
								}
							}}
							value={filter}
							onChange={(e) => setFilter(e.target.value)}
						/>
					</Stack>
				</Stack>
			)}
			scrollableChildren={(
				<Stack direction="row" sx={{ flexWrap: 'wrap', gap: '30px 41px' }}>
					{!loading && (
						<>
							{filteredAlbums?.map((album, index) => (
								<AlbumTile key={`album-${index}`} album={album} />
							))}
						</>
					)}
					{loading && (
						<>
							{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
								<Skeleton key={`skel-${i}`} variant="rounded" width={350} height={431} />
							))}
						</>
					)}
				</Stack>
			)}
		/>
	);
};

export default AlbumScreen;
